import {Helmet} from '@dr.pogodin/react-helmet';
import {skipToken} from '@reduxjs/toolkit/query';
import {
  Alert,
  Card,
  DataStatus,
  Parameter,
  Wizard,
  WizardRef,
  WizardStepConfig,
} from 'platform/components';
import {Box, Clickable, Grid, Heading, Show, Text, VStack} from 'platform/foundation';
import {Lightbox} from 'platform/lightbox';
import {match} from 'ts-pattern';

import {ReactNode, useRef, useState} from 'react';

import {head, isEmpty, isNil, isNotNil} from 'ramda';

import {useCreateRecordAuditMutation, useListRecordAuditDefinitionQuery} from '@dms/api/core';
import {useGetCurrentUserInfoQuery, useGetEmployeeLinkQuery} from '@dms/api/customer';
import {EntityResourceIds, RecordIdHttpModel} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {recordAuditRoutes, settingsRoutes} from '@dms/routes';
import {
  DmsDataGrid,
  EntityHeaderAction,
  EntityHeader,
  Page,
  RECORD_AUDIT_PAIR_GRID_CODES,
  RECORD_AUDIT_PAIR_RESOURCE_ICONS,
  getRecordAuditPairResourceLabel,
  handleApiError,
  isRecordAuditPairResourceId,
  useBranches,
  useLicensedRecordAuditPairResources,
  useVehicleHeaderImage,
} from '@dms/shared';

import {ApiError, buildArray, composePath, getSafely, useNavigate, useQueryState} from 'shared/web';

import {useRecordAuditHeaderSnippets} from '../../components/headerSnippets/useRecordAuditHeaderSnippets';
import {RECORD_AUDIT_ICON} from '../../constants/RECORD_AUDIT_ICON';
import {toCreateRecordAuditBody} from '../../utils/toCreateRecordAuditBody';
import {RecordAuditResourceCard} from './components/RecordAuditResourceCard';

const RECORD_RESOURCE_ID_QUERY_PARAM = 'recordResourceId';
const RECORD_ID_QUERY_PARAM = 'recordId';

const getAppliesToLabel = (resourceIds: string[]): string =>
  isEmpty(resourceIds)
    ? i18n.t('page.recordAudit.create.wizard.anyResource')
    : resourceIds
        .map((id) => (isRecordAuditPairResourceId(id) ? getRecordAuditPairResourceLabel(id) : id))
        .join(', ');

// eslint-disable-next-line complexity, max-statements -- Pre-existing violation
export function RecordAuditCreate() {
  const navigate = useNavigate();
  const [resourceIdParam] = useQueryState(RECORD_RESOURCE_ID_QUERY_PARAM);
  const [recordIdParam] = useQueryState(RECORD_ID_QUERY_PARAM);

  const {data: userInfo, isLoading: isUserInfoLoading} = useGetCurrentUserInfoQuery();
  const userId = userInfo?.id;
  const {data: employeeLink, isLoading: isEmployeeLinkLoading} = useGetEmployeeLinkQuery(
    isNotNil(userId) ? {contextId: userId, contextTarget: 'USER'} : skipToken
  );
  const branchesQuery = useBranches();
  const definitionsQuery = useListRecordAuditDefinitionQuery({});
  const [createAudit, createAuditQuery] = useCreateRecordAuditMutation();

  const licensedResourceIds = useLicensedRecordAuditPairResources();

  // Deep links may carry a resource the tenant is not licensed for — drop them
  // so the wizard starts unselected instead of offering an unlicensed target.
  const initialResourceChoice =
    isRecordAuditPairResourceId(resourceIdParam) && licensedResourceIds.includes(resourceIdParam)
      ? resourceIdParam
      : null;
  const initialRecord: RecordIdHttpModel | null =
    isNotNil(initialResourceChoice) && isNotNil(recordIdParam)
      ? {resourceId: initialResourceChoice, id: recordIdParam}
      : null;

  const [resourceChoice, setResourceChoice] = useState<string | null>(initialResourceChoice);
  const [pickedRecord, setPickedRecord] = useState<RecordIdHttpModel | null>(initialRecord);
  const [definitionId, setDefinitionId] = useState<string | null>(null);
  const [branchId, setBranchId] = useState<string | null>(null);

  // Drive the wizard from the header actions (#9): the buried footer bar is
  // removed and Cancel/Continue live in the entity header top-right.
  const wizardRef = useRef<WizardRef>(null);
  const [currentStep, setCurrentStep] = useState(0);

  const effectiveBranchId =
    branchId ?? userInfo?.settings?.branch ?? userInfo?.defaultBranch?.id ?? '';
  // The auditor is keyed by employee id (not user id); pre-fill it from the current
  // user's employee link when one exists. It is optional — users without a linked
  // employee (e.g. admins) can still create an audit and assign the auditor later on
  // the detail page.
  const auditorEmployeeId = head(employeeLink ?? [])?.employeeId ?? null;

  const isEntitySatisfied = isNotNil(pickedRecord);
  const isReady =
    isNotNil(resourceChoice) &&
    isEntitySatisfied &&
    isNotNil(definitionId) &&
    !isEmpty(effectiveBranchId);

  const {snippets, vehicleId, recordTitle} = useRecordAuditHeaderSnippets({
    testIdPrefix: 'recordAuditCreate',
    record: pickedRecord,
    isPairEditable: false,
    onPair: () => undefined,
    branchId: effectiveBranchId,
    isBranchEditable: true,
    onSelectBranch: setBranchId,
  });

  const [
    coverLightboxControls,
    coverPhotoUrl,
    onCoverPhotoClick,
    vehicleImages,
    photoDownloadSizeDialog,
  ] = useVehicleHeaderImage(vehicleId);

  const handleResourceSelect = (value: string | null) => {
    // Re-selecting the active resource is a no-op (mirrors Radio change-only
    // semantics) so the downstream entity/definition picks are not reset.
    if (value === resourceChoice) {
      return;
    }
    setResourceChoice(value);
    setPickedRecord(null);
    setDefinitionId(null);
  };

  const handleConfirm = () => {
    if (!isReady || isNil(definitionId)) {
      return;
    }
    createAudit({
      body: toCreateRecordAuditBody({
        values: {
          auditorEmployeeId,
          responsiblePersonEmployeeId: null,
          date: new Date(),
          branchId: effectiveBranchId,
          participants: [],
        },
        recordAuditDefinitionId: definitionId,
        pairedRecord: pickedRecord,
      }),
    })
      .unwrap()
      // No success toast: navigating to the new audit detail already reflects the result.
      .then((response) => {
        navigate(composePath(recordAuditRoutes.detail, {params: {id: response.id}}));
      })
      .catch((error: ApiError) => handleApiError(error));
  };

  // Filter audit definitions by the picked resource. A definition with no
  // resourceIds is universal (usable for any resource).
  const selectedResourceId = isRecordAuditPairResourceId(resourceChoice) ? resourceChoice : null;
  const eligibleDefinitions = (definitionsQuery.data ?? []).filter(
    (definition) =>
      isEmpty(definition.resourceIds) ||
      (isNotNil(selectedResourceId) && definition.resourceIds.includes(selectedResourceId))
  );

  const definitionOptions = eligibleDefinitions.map((definition) => ({
    label: definition.name,
    value: definition.id,
  }));

  // Once definitions have loaded, a picked resource with no eligible definition cannot proceed.
  // Surfaced as a warning already in step 1 so the user can create one before continuing, and as
  // the empty state of step 3.
  const hasNoEligibleDefinition =
    isNotNil(resourceChoice) && !definitionsQuery.isLoading && isEmpty(eligibleDefinitions);

  const openCreateDefinition = () => navigate(settingsRoutes.recordAuditDefinitionCreate);

  const nothingSelectedLabel = i18n.t('general.labels.selectedNothing');

  const resourceSubtitle = isRecordAuditPairResourceId(resourceChoice)
    ? getRecordAuditPairResourceLabel(resourceChoice)
    : nothingSelectedLabel;

  const entitySubtitle = isEntitySatisfied
    ? (recordTitle ?? nothingSelectedLabel)
    : nothingSelectedLabel;

  const definitionSubtitle = isNotNil(definitionId)
    ? (definitionOptions.find((option) => option.value === definitionId)?.label ??
      nothingSelectedLabel)
    : nothingSelectedLabel;

  const entityStepContent = isRecordAuditPairResourceId(resourceChoice) ? (
    <StepSection
      heading={i18n.t('page.recordAudit.create.wizard.steps.entity.title')}
      description={i18n.t('page.recordAudit.create.wizard.steps.entity.description')}
    >
      <DmsDataGrid
        key={resourceChoice}
        gridCode={getSafely(RECORD_AUDIT_PAIR_GRID_CODES, resourceChoice) ?? ''}
        onRowSelectionChange={(rows) => {
          const rowId = head(rows)?.id ?? null;
          setPickedRecord(isNotNil(rowId) ? {resourceId: resourceChoice, id: rowId} : null);
          setDefinitionId(null);
        }}
      />
    </StepSection>
  ) : (
    <Box padding={4} />
  );

  const steps: WizardStepConfig[] = [
    {
      title: i18n.t('page.recordAudit.create.wizard.steps.resource.stepTitle'),
      subtitle: resourceSubtitle,
      severity: isNotNil(resourceChoice) ? 'success' : undefined,
      content: (
        <StepSection
          heading={i18n.t('page.recordAudit.create.wizard.steps.resource.title')}
          description={i18n.t('page.recordAudit.create.wizard.steps.resource.description')}
        >
          <Show when={hasNoEligibleDefinition}>
            <Alert
              variant="warning"
              layout="detailed"
              title={i18n.t('page.recordAudit.create.wizard.noDefinition.title')}
              message={i18n.t('page.recordAudit.create.wizard.noDefinition.description')}
              actions={[
                {
                  title: i18n.t('page.recordAudit.create.wizard.noDefinition.action'),
                  variant: 'link',
                  leftIcon: 'content/add_circle',
                  onClick: openCreateDefinition,
                  'data-testid': 'recordAuditCreate-noDefinition-create',
                },
              ]}
              data-testid="recordAuditCreate-noDefinition"
            />
          </Show>
          <Grid columns={[1, 2, 3]} spacing={3}>
            {licensedResourceIds.map((id) => (
              <RecordAuditResourceCard
                key={id}
                icon={RECORD_AUDIT_PAIR_RESOURCE_ICONS[id]}
                label={getRecordAuditPairResourceLabel(id)}
                isSelected={id === resourceChoice}
                onSelect={() => handleResourceSelect(id)}
                data-testid={`recordAuditCreate-resource-${id}`}
              />
            ))}
          </Grid>
        </StepSection>
      ),
    },
    {
      title: i18n.t('page.recordAudit.create.wizard.steps.entity.stepTitle'),
      subtitle: entitySubtitle,
      isDisabled: isNil(resourceChoice),
      severity: isEntitySatisfied ? 'success' : undefined,
      content: entityStepContent,
    },
    {
      title: i18n.t('page.recordAudit.create.wizard.steps.definition.stepTitle'),
      subtitle: definitionSubtitle,
      isDisabled: !isEntitySatisfied,
      severity: isNotNil(definitionId) ? 'success' : undefined,
      content: (
        <StepSection
          heading={i18n.t('page.recordAudit.create.wizard.steps.definition.title')}
          description={i18n.t('page.recordAudit.create.wizard.steps.definition.description')}
        >
          <DataStatus
            isLoading={definitionsQuery.isLoading}
            isEmpty={eligibleDefinitions.length === 0}
            emptyMessage={i18n.t('page.recordAudit.create.wizard.noDefinition.title')}
            emptySubheadline={i18n.t('page.recordAudit.create.wizard.noDefinition.description')}
          >
            <VStack spacing={3}>
              {eligibleDefinitions.map((definition) => (
                <Clickable
                  key={definition.id}
                  width="100%"
                  onClick={() => setDefinitionId(definition.id)}
                  data-testid={`recordAuditCreate-definition-${definition.id}`}
                >
                  <Card
                    variant="inlineWhite"
                    title={definition.name}
                    subtitle={definition.description ?? undefined}
                    flags={
                      isNotNil(definition.category)
                        ? [{label: definition.category, colorScheme: 'neutral', isSubtle: true}]
                        : undefined
                    }
                    parameters={buildArray<Parameter>()
                      .add(
                        i18n.t('page.recordAudit.create.wizard.taskCount', {
                          count: definition.tasks.length,
                        })
                      )
                      .add(
                        i18n.t('page.recordAudit.create.wizard.passThreshold', {
                          threshold: definition.successThreshold,
                        })
                      )
                      .add(getAppliesToLabel(definition.resourceIds))}
                    control={{
                      type: 'radio',
                      value: definition.id === definitionId,
                      onChange: () => setDefinitionId(definition.id),
                    }}
                  />
                </Clickable>
              ))}
            </VStack>
          </DataStatus>
        </StepSection>
      ),
    },
  ];

  const isLastStep = currentStep === steps.length - 1;

  // The Continue button advances the wizard until the final step, where it
  // becomes the Confirm (create) action. Gating mirrors each step's prerequisite.
  const canContinue = match(currentStep)
    // The step-1 warning tells the user there is no eligible definition, but does not block: they
    // can still progress (they have been warned) and the definition step shows its empty state.
    .with(0, () => isNotNil(resourceChoice))
    .with(1, () => isEntitySatisfied)
    .otherwise(() => false);

  const headerActions = buildArray<EntityHeaderAction>()
    .add({
      type: 'button',
      variant: 'secondary',
      title: i18n.t('general.actions.cancel'),
      onClick: () => navigate(recordAuditRoutes.overview),
      'data-testid': 'recordAuditCreate-cancel',
    })
    .add(
      match<boolean, EntityHeaderAction>(isLastStep)
        .with(true, () => ({
          type: 'button',
          variant: 'secondary',
          title: i18n.t('general.actions.confirm'),
          onClick: handleConfirm,
          isDisabled: !isReady,
          isLoading: createAuditQuery.isLoading,
          'data-testid': 'recordAuditCreate-confirm',
        }))
        .with(false, () => ({
          type: 'button',
          variant: 'secondary',
          title: i18n.t('general.actions.continue'),
          // The wizard ref setter does not fire onStepChange, so keep the local
          // step (which drives this action) in sync explicitly.
          onClick: () => {
            const nextStep = currentStep + 1;
            wizardRef.current?.setCurrentStep(nextStep);
            setCurrentStep(nextStep);
          },
          isDisabled: !canContinue,
          'data-testid': 'recordAuditCreate-continue',
        }))
        .exhaustive()
    );

  return (
    <>
      <Helmet title={i18n.t('module.recordAudit.title')} />
      <Page
        isFullHeight
        data-testid="recordAuditCreate-page"
        header={
          <EntityHeader
            variant="create"
            data-testid="recordAuditCreate-header"
            resourceId={EntityResourceIds.recordAudit}
            title={i18n.t('page.recordAudit.actions.newAudit')}
            image={isNotNil(coverPhotoUrl) ? coverPhotoUrl : undefined}
            icon={isNotNil(coverPhotoUrl) ? undefined : RECORD_AUDIT_ICON}
            onImageClick={isNotNil(coverPhotoUrl) ? onCoverPhotoClick : undefined}
            flags={[
              {label: i18n.t('page.recordAudit.create.wizard.draftFlag'), colorScheme: 'neutral'},
            ]}
            snippets={snippets}
            actions={headerActions}
          />
        }
      >
        <Box flex={1} minWidth={0} height="100%" backgroundColor="text.white">
          <DataStatus
            isLoading={isUserInfoLoading || isEmployeeLinkLoading || branchesQuery.isLoading}
            minHeight="100%"
          >
            <Wizard
              ref={wizardRef}
              data-testid="recordAuditCreate"
              orientation="horizontal"
              steps={steps}
              width="100%"
              height="100%"
              onStepChange={setCurrentStep}
            />
          </DataStatus>
        </Box>
      </Page>
      <Lightbox
        data-testid="recordAuditCreate-coverLightbox"
        controls={coverLightboxControls}
        images={vehicleImages}
      />
      {photoDownloadSizeDialog}
    </>
  );
}

interface StepSectionProps {
  heading: string;
  description: string;
  children: ReactNode;
}

function StepSection(props: StepSectionProps) {
  return (
    <Box padding={4} height="100%">
      <VStack spacing={4} height="100%">
        <VStack spacing={1}>
          <Heading size={3}>{props.heading}</Heading>
          <Text size="small" color="secondary">
            {props.description}
          </Text>
        </VStack>
        {props.children}
      </VStack>
    </Box>
  );
}
